/**
 * Annual revenue / net-income bar-chart geometry (F14, plan §1.6 · §3.3).
 *
 * Pure numbers, no DOM — a component receives coordinates and never computes
 * them. It lives beside `chart-path.ts` rather than in `apps/web` so it carries
 * core's 95% gate and fast-check invariants instead of web's 80%.
 *
 * Label anchors ride on the plot box for the same reason `ChartLabelAnchors`
 * does: a literal `height - 8` in the JSX does not move when the padding
 * changes, so the ticks silently drift off the plot area.
 */

export interface AnnualPeriod {
  readonly year: number;
  readonly value: number | null;
}

/** Anchor points for the four text/marker families the component draws. */
export interface AnnualLabelAnchors {
  /** Right-aligned x for the value ticks, left of the plot area. */
  readonly axisX: number;
  /** Baseline nudge that centres a value tick on its gridline. */
  readonly axisDy: number;
  /** Baseline for the year ticks, under the plot area. */
  readonly yearY: number;
  /** Distance a YoY label sits above its dot. */
  readonly yoyDy: number;
  /** Radius of the value dot on the trend line. */
  readonly dotRadius: number;
}

export interface AnnualPlotBox {
  readonly x: number;
  readonly y: number;
  readonly w: number;
  readonly h: number;
  readonly min: number;
  readonly max: number;
  readonly labels: AnnualLabelAnchors;
}

export interface AnnualBar {
  readonly year: number;
  readonly value: number | null;
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly height: number;
  readonly cx: number;
  /** Null for a missing year — the slot is reserved, never plotted at zero. */
  readonly cy: number | null;
  readonly yoy: number | null;
}

export const ANNUAL_CHART_VIEW = { width: 640, height: 200 } as const;
export const ANNUAL_CHART_PAD = { l: 48, r: 12, t: 28, b: 28 } as const;

/** Gap between a value tick's right edge and the plot area. */
export const ANNUAL_AXIS_LABEL_GAP = 6;
/** Baseline nudge that centres a value tick on its gridline. */
export const ANNUAL_AXIS_LABEL_DY = 3;
/** Gap between the plot area's bottom edge and the year-tick baseline. */
export const ANNUAL_YEAR_LABEL_GAP = 20;

/** Baseline inset of the year tick row from the bottom of the viewBox. */
export const ANNUAL_YEAR_LABEL_BASELINE = 8;
/** Distance a YoY label sits above its dot. */
export const ANNUAL_YOY_LABEL_DY = 8;
/** Radius of the value dot on the trend line. */
export const ANNUAL_DOT_RADIUS = 3;
/** Bar width as a fraction of its year slot. */
export const ANNUAL_BAR_WIDTH_RATIO = 0.5;

export function yoyGrowth(prev: number | null, curr: number | null): number | null {
  if (prev === null || curr === null) return null;
  if (!Number.isFinite(prev) || !Number.isFinite(curr) || prev <= 0) return null;
  if (curr < 0) return null;
  const growth = curr / prev - 1;
  return Number.isFinite(growth) ? growth : null;
}

/** True when at least one period carries a finite value (R2b: not "has rows"). */
export function hasAnnualValue(periods: readonly AnnualPeriod[]): boolean {
  return periods.some((period) => period.value !== null && Number.isFinite(period.value));
}

function isFiniteValue(period: AnnualPeriod): period is AnnualPeriod & { value: number } {
  return period.value !== null && Number.isFinite(period.value);
}

function finiteValues(periods: readonly AnnualPeriod[]): readonly number[] {
  return periods.filter(isFiniteValue).map((period) => period.value);
}

function extent(values: readonly number[]): { readonly min: number; readonly max: number } {
  const seed = values[0] ?? 0;
  return values.reduce(
    (acc, value) => ({
      min: value < acc.min ? value : acc.min,
      max: value > acc.max ? value : acc.max,
    }),
    { min: seed, max: seed },
  );
}

function includeZero(range: { readonly min: number; readonly max: number }): {
  readonly min: number;
  readonly max: number;
} {
  const min = range.min > 0 ? 0 : range.min;
  const max = range.max < 0 ? 0 : range.max;
  if (max === min) return { min, max: min + 1 };
  return { min, max };
}

export function annualValueDomain(periods: readonly AnnualPeriod[]): {
  readonly min: number;
  readonly max: number;
} {
  const finite = finiteValues(periods);
  if (finite.length === 0) return { min: 0, max: 1 };
  return includeZero(extent(finite));
}

export function annualPlotBox(periods: readonly AnnualPeriod[]): AnnualPlotBox {
  const domain = annualValueDomain(periods);
  const x = ANNUAL_CHART_PAD.l;
  const y = ANNUAL_CHART_PAD.t;
  const h = ANNUAL_CHART_VIEW.height - ANNUAL_CHART_PAD.t - ANNUAL_CHART_PAD.b;
  return {
    x,
    y,
    w: ANNUAL_CHART_VIEW.width - ANNUAL_CHART_PAD.l - ANNUAL_CHART_PAD.r,
    h,
    min: domain.min,
    max: domain.max,
    labels: {
      axisX: x - ANNUAL_AXIS_LABEL_GAP,
      axisDy: ANNUAL_AXIS_LABEL_DY,
      yearY: y + h + ANNUAL_YEAR_LABEL_GAP,
      yoyDy: ANNUAL_YOY_LABEL_DY,
      dotRadius: ANNUAL_DOT_RADIUS,
    },
  };
}

export function annualYAt(value: number, box: AnnualPlotBox): number {
  const span = box.max - box.min;
  return box.y + ((box.max - value) / span) * box.h;
}

function barRect(
  value: number,
  slotX: number,
  slotW: number,
  box: AnnualPlotBox,
): Pick<AnnualBar, 'x' | 'y' | 'width' | 'height' | 'cx' | 'cy'> {
  const width = slotW * ANNUAL_BAR_WIDTH_RATIO;
  const x = slotX + (slotW - width) / 2;
  const zero = annualYAt(0, box);
  const top = annualYAt(value, box);
  if (value >= 0) {
    return { x, y: top, width, height: Math.max(0, zero - top), cx: x + width / 2, cy: top };
  }
  return { x, y: zero, width, height: Math.max(0, top - zero), cx: x + width / 2, cy: top };
}

function emptySlot(
  period: AnnualPeriod,
  slotX: number,
  slotW: number,
  box: AnnualPlotBox,
): AnnualBar {
  return {
    year: period.year,
    value: null,
    x: slotX,
    y: annualYAt(0, box),
    width: slotW * ANNUAL_BAR_WIDTH_RATIO,
    height: 0,
    cx: slotX + slotW / 2,
    cy: null,
    yoy: null,
  };
}

export function layoutAnnualBars(
  periods: readonly AnnualPeriod[],
  box: AnnualPlotBox,
): readonly AnnualBar[] {
  const n = periods.length;
  if (n === 0) return [];
  const slotW = box.w / n;
  return periods.map((period, i) => {
    const slotX = box.x + i * slotW;
    const prior = i === 0 ? undefined : periods[i - 1];
    /* YoY only against the IMMEDIATELY preceding fiscal year — a gap in the
       series is not a comparison. */
    const prev = prior !== undefined && prior.year + 1 === period.year ? prior.value : null;
    if (!isFiniteValue(period)) return emptySlot(period, slotX, slotW, box);
    return {
      year: period.year,
      value: period.value,
      ...barRect(period.value, slotX, slotW, box),
      yoy: yoyGrowth(prev, period.value),
    };
  });
}

/**
 * Trend line over the plotted tops. A missing year BREAKS the line into a new
 * `M` subpath: joining across it drew one segment straight through the empty
 * slot, which reads as an interpolated value rather than a gap (R2b).
 */
export function annualLinePath(bars: readonly AnnualBar[]): string | null {
  const parts = bars.reduce<{ readonly cmds: readonly string[]; readonly penDown: boolean }>(
    (acc, bar) => {
      if (bar.cy === null) return { cmds: acc.cmds, penDown: false };
      const cmd = `${acc.penDown ? 'L' : 'M'} ${String(bar.cx)} ${String(bar.cy)}`;
      return { cmds: [...acc.cmds, cmd], penDown: true };
    },
    { cmds: [], penDown: false },
  );
  return parts.cmds.length === 0 ? null : parts.cmds.join(' ');
}
